import { PageClassSelector } from '../../../components/layout/TeacherWorkspace';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PlanCompletionSummary } from '../../../components/v2/teacher/PlanCompletionSummary';
import { preferredTeacherSection, rememberTeacherSection } from '../../../services/teacherSection';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { TeachingDay } from '../../../components/v2/teacher/TeachingDay';
import { assignmentsApi, sectionsApi, hooksV2Api, lessonPlansApi, lessonRunsApi } from '../../../services/apiV2';
import { Hook, LessonPlan, LessonRun, LessonSchedule, PrintQueueItem, Section } from '../../../types/v2';
import TeacherNav from '../../../components/TeacherNav';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { OptionalAppShell } from '../../../components/layout/AppShell';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { PageHeader } from '../../../components/layout/PageHeader';
import { SectionCard } from '../../../components/layout/SectionCard';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Checkbox } from '../../../components/ui/checkbox';
import { Input } from '../../../components/ui/input';
import { cn } from '../../../lib/cn';
import { lessonResourceSourceLabel, lessonResourceSourceType } from '../../../utils/lessonResources';
import { ResourceOperationsPanel } from '../../../components/v2/ResourceOperationsPanel';

const RECENT_RUNS_LIMIT = 5;

type AssignmentTargetScope = 'all' | 'custom' | 'group';
type LaunchTargetMode = 'live' | 'split' | 'replay_lesson' | 'replay_exam' | 'assessment' | 'practice';

interface SectionStudentRow {
  id: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  group_name?: string;
}

const formatRunStarted = (timestamp?: string | null): string => {
  if (!timestamp) return 'Unknown start time';
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return 'Unknown start time';
  return parsed.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const compareRunsByStartedAtDesc = (a: LessonRun, b: LessonRun): number => {
  const left = Date.parse(String(a.started_at || ''));
  const right = Date.parse(String(b.started_at || ''));
  if (!Number.isNaN(right) && !Number.isNaN(left) && right !== left) {
    return right - left;
  }
  return Number(b.id || 0) - Number(a.id || 0);
};

const formatRunStatus = (status?: string | null): string => {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'completed') return 'Completed';
  if (normalized === 'paused') return 'Paused';
  if (normalized === 'active') return 'Active';
  return normalized ? `${normalized[0].toUpperCase()}${normalized.slice(1)}` : 'Unknown';
};

const runStatusBadgeVariant = (status?: string | null): 'default' | 'success' | 'warning' | 'muted' => {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'completed') return 'success';
  if (normalized === 'paused') return 'warning';
  if (normalized === 'active') return 'default';
  return 'muted';
};

const assignmentModeLabel = (deliveryMode: LaunchTargetMode): string => (
  deliveryMode === 'live'
    ? 'live lesson'
    : deliveryMode === 'split'
      ? 'split class'
      : deliveryMode === 'assessment'
        ? 'assessment'
      : deliveryMode === 'replay_exam'
        ? 'quiz'
        : 'lesson'
);

const DashboardMetric: React.FC<{
  label: string;
  value: React.ReactNode;
  detail: React.ReactNode;
  accentClassName: string;
}> = ({ label, value, detail, accentClassName }) => (
  <Card className="overflow-hidden">
    <div className={`h-1.5 ${accentClassName}`} />
    <CardContent className="space-y-2 pt-4">
      <p className="text-xs uppercase tracking-[0.18em] text-muted">{label}</p>
      <p className="text-3xl font-semibold tracking-tight text-text">{value}</p>
      <p className="text-sm text-muted">{detail}</p>
    </CardContent>
  </Card>
);

type LessonWeekGroup = {
  key: string;
  label: string;
  plans: LessonPlan[];
};

const groupPlansByCurriculumWeek = (plans: LessonPlan[]): LessonWeekGroup[] => {
  const groups = new Map<string, LessonWeekGroup>();
  plans.forEach((plan) => {
    const weekNumber = Number(plan.week_number);
    const hasWeek = Number.isInteger(weekNumber) && weekNumber >= 1 && weekNumber <= 52;
    const key = hasWeek ? `week-${weekNumber}` : 'week-not-set';
    const label = hasWeek ? `Week ${weekNumber}` : 'Week not set';
    const existing = groups.get(key);
    if (existing) {
      existing.plans.push(plan);
    } else {
      groups.set(key, { key, label, plans: [plan] });
    }
  });
  return Array.from(groups.values()).sort((left, right) => {
    if (left.key === 'week-not-set') return 1;
    if (right.key === 'week-not-set') return -1;
    return Number(left.key.replace('week-', '')) - Number(right.key.replace('week-', ''));
  });
};

const formatPlannedDate = (value: string): string => {
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

// The embedded assignment picker reuses the existing delivery and targeting APIs.
// Live teaching is launched from the agenda, never from this picker.
export const V2SectionDashboard: React.FC<{ assignmentsOnly?: boolean }> = ({ assignmentsOnly = false }) => {
  const [assignmentChoice, setAssignmentChoice] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const redesignEnabled = isUiRedesignPageEnabled('v2_teacher_sections');
  const [sections, setSections] = useState<Section[]>([]);
  const [hooks, setHooks] = useState<Hook[]>([]);
  const [lessonPlans, setLessonPlans] = useState<LessonPlan[]>([]);
  const [activeRunsBySection, setActiveRunsBySection] = useState<Record<number, LessonRun>>({});
  const [recentRunsBySection, setRecentRunsBySection] = useState<Record<number, LessonRun[]>>({});
  const [loading, setLoading] = useState(false);
  const [loadingActiveRuns, setLoadingActiveRuns] = useState(false);
  const [loadingRunHistory, setLoadingRunHistory] = useState(false);
  const [endingRunId, setEndingRunId] = useState<number | null>(null);
  const [assigningPlanKey, setAssigningPlanKey] = useState<string | null>(null);
  const [assignmentTargetOpen, setAssignmentTargetOpen] = useState(false);
  const [assignmentTargetPlan, setAssignmentTargetPlan] = useState<LessonPlan | null>(null);
  const [assignmentTargetSection, setAssignmentTargetSection] = useState<Section | null>(null);
  const [assignmentTargetMode, setAssignmentTargetMode] = useState<LaunchTargetMode>('replay_lesson');
  const [assessmentStage, setAssessmentStage] = useState<'define' | 'do' | 'explain' | 'apply'>('define');
  const [applyStandards, setApplyStandards] = useState('');
  const [assessmentRetakePolicy, setAssessmentRetakePolicy] = useState<'teacher_approval' | 'disabled'>('teacher_approval');
  const [assessmentRetakeRemediation, setAssessmentRetakeRemediation] = useState('');
  const [assignmentTargetScope, setAssignmentTargetScope] = useState<AssignmentTargetScope>('all');
  const [assignmentTargetStudents, setAssignmentTargetStudents] = useState<SectionStudentRow[]>([]);
  const [assignmentTargetSelectedStudentIds, setAssignmentTargetSelectedStudentIds] = useState<number[]>([]);
  const [assignmentTargetGroupName, setAssignmentTargetGroupName] = useState('');
  const [assignmentTargetLoading, setAssignmentTargetLoading] = useState(false);
  const [assignmentTargetError, setAssignmentTargetError] = useState('');
  const [materialsPlan, setMaterialsPlan] = useState<LessonPlan | null>(null);
  const [pendingPrintByPlan, setPendingPrintByPlan] = useState<Record<number, number>>({});
  const [lessonSchedules, setLessonSchedules] = useState<LessonSchedule[]>([]);
  const [printQueue, setPrintQueue] = useState<PrintQueueItem[]>([]);
  const [preparationLoading, setPreparationLoading] = useState(false);
  const [preparationError, setPreparationError] = useState('');
  const [updatingScheduleKey, setUpdatingScheduleKey] = useState('');
  const [printingResourceKey, setPrintingResourceKey] = useState('');
  const [newSectionName, setNewSectionName] = useState('');
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);
  useEffect(() => { rememberTeacherSection(selectedSectionId); }, [selectedSectionId]);
  const [selectedLessonWeekBySection, setSelectedLessonWeekBySection] = useState<Record<number, string>>({});
  const [runError, setRunError] = useState('');
  const [teacherNotice, setTeacherNotice] = useState('');
  const [startInTestMode, setStartInTestMode] = useState(false);

  const normalizeArray = useCallback((data: any) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    return [];
  }, []);

  const loadPreparation = useCallback(async () => {
    setPreparationLoading(true);
    setPreparationError('');
    try {
      const [schedulesResponse, queueResponse] = await Promise.all([
        lessonPlansApi.schedules(),
        lessonPlansApi.printQueue(7),
      ]);
      setLessonSchedules(Array.isArray(schedulesResponse.data?.schedules) ? schedulesResponse.data.schedules : []);
      setPrintQueue(Array.isArray(queueResponse.data?.items) ? queueResponse.data.items : []);
    } catch (error: any) {
      setPreparationError(error?.response?.data?.detail || 'Unable to load lesson preparation dates.');
    } finally {
      setPreparationLoading(false);
    }
  }, []);

  const loadActiveRuns = useCallback(async (sectionRows: Section[]) => {
    if (!sectionRows.length) {
      setActiveRunsBySection({});
      return;
    }

    setLoadingActiveRuns(true);
    try {
      const runPairs = await Promise.all(
        sectionRows.map(async (section) => {
          try {
            const run = await lessonRunsApi.active(section.id);
            return [section.id, run] as const;
          } catch {
            return [section.id, null] as const;
          }
        }),
      );

      const nextActiveRuns: Record<number, LessonRun> = {};
      runPairs.forEach(([sectionId, run]) => {
        if (run?.id) {
          nextActiveRuns[sectionId] = run;
        }
      });
      setActiveRunsBySection(nextActiveRuns);
    } finally {
      setLoadingActiveRuns(false);
    }
  }, []);

  const loadRecentRuns = useCallback(async (sectionRows: Section[]) => {
    if (!sectionRows.length) {
      setRecentRunsBySection({});
      return;
    }

    setLoadingRunHistory(true);
    try {
      const sectionIds = new Set(sectionRows.map((section) => Number(section.id)));
      const response = await lessonRunsApi.list();
      const runRows: LessonRun[] = normalizeArray(response.data)
        .filter((row: any) => {
          if (!row || typeof row !== 'object') return false;
          const runId = Number(row.id);
          const sectionId = Number(row.section);
          if (!Number.isInteger(runId) || runId <= 0) return false;
          if (!Number.isInteger(sectionId) || !sectionIds.has(sectionId)) return false;
          if (Boolean(row.is_test_mode) && !Boolean(row.reports_included)) return false;
          return true;
        })
        .map((row: any): LessonRun => row as LessonRun)
        .sort(compareRunsByStartedAtDesc);

      const grouped: Record<number, LessonRun[]> = {};
      runRows.forEach((run: LessonRun) => {
        if (String(run.status || '').trim().toLowerCase() === 'active') return;
        const sectionId = Number(run.section);
        if (!Number.isInteger(sectionId)) return;
        if (!grouped[sectionId]) grouped[sectionId] = [];
        if (grouped[sectionId].length >= RECENT_RUNS_LIMIT) return;
        grouped[sectionId].push(run);
      });
      setRecentRunsBySection(grouped);
    } catch {
      setRecentRunsBySection({});
    } finally {
      setLoadingRunHistory(false);
    }
  }, [normalizeArray]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [sectionsRes, hooksRes, plansRes] = await Promise.all([
          sectionsApi.list(),
          hooksV2Api.list(),
          lessonPlansApi.list(),
        ]);
        const sectionsData = normalizeArray(sectionsRes.data).filter((section: Section) => section.is_active !== false);
        const hooksData = normalizeArray(hooksRes.data);
        const plansData = normalizeArray(plansRes.data);
        setSections(sectionsData);
        setHooks(hooksData);
        setLessonPlans(plansData.filter((plan: LessonPlan) => !plan.is_archived));
        if (!assignmentsOnly) await Promise.all([
          loadActiveRuns(sectionsData),
          loadRecentRuns(sectionsData),
          loadPreparation(),
        ]);
      } catch {
        setRunError('Could not load saved content. Refresh to try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [assignmentsOnly, loadActiveRuns, loadPreparation, loadRecentRuns, normalizeArray]);

  useEffect(() => {
    const notice = (location.state as any)?.teacher_notice;
    if (typeof notice === 'string' && notice.trim()) {
      setTeacherNotice(notice.trim());
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (assignmentsOnly) return;
    const handleWindowFocus = () => {
      void loadActiveRuns(sections);
      void loadRecentRuns(sections);
    };
    window.addEventListener('focus', handleWindowFocus);
    return () => window.removeEventListener('focus', handleWindowFocus);
  }, [assignmentsOnly, loadActiveRuns, loadRecentRuns, sections]);

  useEffect(() => {
    setSelectedSectionId((current) => {
      if (!sections.length) return null;
      if (current !== null && sections.some((section) => section.id === current)) {
        return current;
      }
      return preferredTeacherSection(sections);
    });
  }, [sections]);

  const startRun = async (
    planId: number,
    sectionId: number,
    targetOptions?: {
      target_scope?: AssignmentTargetScope;
      target_student_ids?: number[];
      target_group_name?: string;
    },
  ) => {
    setRunError('');
    try {
      try {
        const activeRun = await lessonRunsApi.active(sectionId);
        if (activeRun?.id) {
          if (Boolean(activeRun.is_test_mode) !== Boolean(startInTestMode)) {
            await lessonRunsApi.setTestMode(activeRun.id, startInTestMode);
          }
          navigate(`/v2/teacher/run/${activeRun.id}`);
          return;
        }
      } catch (err) {
        // No active run exists; continue to start a new one.
      }

      const response = await lessonRunsApi.start({
        lesson_plan: planId,
        section: sectionId,
        is_test_mode: startInTestMode,
        ...(targetOptions || {}),
      });
      navigate(`/v2/teacher/run/${response.data.id}`);
    } catch (err) {
      console.error('Failed to start run:', err);
      setRunError('Unable to start lesson run. Please try again.');
    }
  };

  const endRun = useCallback(async (run: LessonRun, sectionName: string) => {
    const confirmed = window.confirm(
      'End this live lesson now? Students will no longer see it as active.',
    );
    if (!confirmed) return;

    setRunError('');
    try {
      setEndingRunId(run.id);
      await lessonRunsApi.end(run.id);
      setTeacherNotice(`Live lesson ended for ${sectionName}.`);
      await Promise.all([
        loadActiveRuns(sections),
        loadRecentRuns(sections),
      ]);
    } catch (err: any) {
      setRunError(err?.response?.data?.error || 'Unable to end lesson run. Please try again.');
    } finally {
      setEndingRunId(null);
    }
  }, [loadActiveRuns, loadRecentRuns, sections]);

  const createSection = async () => {
    const trimmedSectionName = newSectionName.trim();
    if (!trimmedSectionName) return;
    const createResponse = await sectionsApi.create({ name: trimmedSectionName });
    setNewSectionName('');
    const sectionsRes = await sectionsApi.list();
    const sectionsData = normalizeArray(sectionsRes.data).filter((section: Section) => section.is_active !== false);
    setSections(sectionsData);
    const createdSectionId = Number((createResponse as any)?.data?.id ?? (createResponse as any)?.id);
    const createdSection = sectionsData.find((section: Section) => (
      section.id === createdSectionId || section.name === trimmedSectionName
    ));
    if (createdSection?.id) {
      setSelectedSectionId(createdSection.id);
    }
    await Promise.all([
      loadActiveRuns(sectionsData),
      loadRecentRuns(sectionsData),
    ]);
  };

  const assignmentTargetGroups = useMemo(() => (
    Array.from(new Set(
      assignmentTargetStudents
        .map((student) => String(student.group_name || '').trim())
        .filter((value) => value.length > 0),
    )).sort((left, right) => left.localeCompare(right))
  ), [assignmentTargetStudents]);

  const assignmentTargetSelectedGroupStudents = useMemo(() => (
    assignmentTargetGroupName
      ? assignmentTargetStudents.filter((student) => String(student.group_name || '').trim() === assignmentTargetGroupName)
      : []
  ), [assignmentTargetGroupName, assignmentTargetStudents]);

  const splitLiveStudentIds = useMemo(() => {
    if (assignmentTargetScope === 'all') {
      return assignmentTargetStudents.map((student) => student.id);
    }
    if (assignmentTargetScope === 'group') {
      return assignmentTargetSelectedGroupStudents.map((student) => student.id);
    }
    return assignmentTargetSelectedStudentIds;
  }, [
    assignmentTargetScope,
    assignmentTargetSelectedGroupStudents,
    assignmentTargetSelectedStudentIds,
    assignmentTargetStudents,
  ]);

  const splitAssignmentStudentIds = useMemo(() => {
    const liveIds = new Set(splitLiveStudentIds);
    return assignmentTargetStudents
      .map((student) => student.id)
      .filter((studentId) => !liveIds.has(studentId));
  }, [assignmentTargetStudents, splitLiveStudentIds]);

  const assignmentTargetStudentName = useCallback((student: SectionStudentRow) => {
    const fullName = `${String(student.first_name || '').trim()} ${String(student.last_name || '').trim()}`.trim();
    return fullName || String(student.email || `Student ${student.id}`);
  }, []);

  const openAssignmentTargetDialog = useCallback(async (
    plan: LessonPlan,
    section: Section,
    deliveryMode: LaunchTargetMode,
  ) => {
    setRunError('');
    setAssignmentTargetPlan(plan);
    setAssignmentTargetSection(section);
    setAssignmentTargetMode(deliveryMode);
    setAssessmentStage('define');
    setApplyStandards('');
    setAssessmentRetakePolicy('teacher_approval');
    setAssessmentRetakeRemediation('');
    setAssignmentTargetScope(deliveryMode === 'split' ? 'custom' : 'all');
    setAssignmentTargetSelectedStudentIds([]);
    setAssignmentTargetGroupName('');
    setAssignmentTargetStudents([]);
    setAssignmentTargetError('');
    setAssignmentTargetOpen(true);
    setAssignmentTargetLoading(true);
    try {
      const response = await sectionsApi.students(section.id);
      const students = Array.isArray(response.data?.students) ? response.data.students : [];
      const normalizedStudents = [...students].sort((left: SectionStudentRow, right: SectionStudentRow) => {
        const leftLast = String(left.last_name || '').toLowerCase();
        const rightLast = String(right.last_name || '').toLowerCase();
        if (leftLast !== rightLast) return leftLast.localeCompare(rightLast);
        const leftFirst = String(left.first_name || '').toLowerCase();
        const rightFirst = String(right.first_name || '').toLowerCase();
        return leftFirst.localeCompare(rightFirst);
      });
      setAssignmentTargetStudents(normalizedStudents);
      const groupNames = Array.from(new Set(
        normalizedStudents
          .map((student: SectionStudentRow) => String(student.group_name || '').trim())
          .filter((value: string) => value.length > 0),
      )).sort((left, right) => left.localeCompare(right));
      setAssignmentTargetGroupName(groupNames[0] || '');
    } catch (err: any) {
      setAssignmentTargetError(err?.response?.data?.error || 'Unable to load section students for assignment targeting.');
    } finally {
      setAssignmentTargetLoading(false);
    }
  }, []);

  const closeAssignmentTargetDialog = useCallback(() => {
    if (assigningPlanKey) return;
    setAssignmentTargetOpen(false);
    setAssignmentTargetError('');
  }, [assigningPlanKey]);

  const toggleAssignmentTargetStudent = useCallback((studentId: number) => {
    setAssignmentTargetSelectedStudentIds((prev) => (
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    ));
  }, []);

  const assignWithoutLive = useCallback(async () => {
    if (!assignmentTargetPlan || !assignmentTargetSection) return;

    const modeLabel = assignmentModeLabel(assignmentTargetMode);
    const key = `${assignmentTargetSection.id}:${assignmentTargetPlan.id}:${assignmentTargetMode}`;
    const targetPayload: {
      target_scope?: AssignmentTargetScope;
      target_student_ids?: number[];
      target_group_name?: string;
    } = {
      target_scope: assignmentTargetScope,
    };
    const assignmentDeliveryMode = (assignmentTargetMode === 'assessment' || assignmentTargetMode === 'practice')
      ? 'replay_lesson'
      : assignmentTargetMode === 'replay_lesson' || assignmentTargetMode === 'replay_exam'
        ? assignmentTargetMode
      : null;
    const payload: {
      section_id: number;
      lesson_plan_id: number;
      delivery_mode: 'replay_lesson' | 'replay_exam';
      is_active: true;
      target_scope?: 'all' | 'custom' | 'group';
      target_student_ids?: number[];
      target_group_name?: string;
      purpose?: 'makeup' | 'assessment' | 'practice';
      assessment_stage?: 'define' | 'do' | 'explain' | 'apply';
      standards_snapshot?: string[];
      retake_policy?: 'teacher_approval' | 'disabled';
      retake_remediation_instructions?: string;
    } | null = assignmentDeliveryMode ? {
      section_id: assignmentTargetSection.id,
      lesson_plan_id: assignmentTargetPlan.id,
      delivery_mode: assignmentDeliveryMode,
      is_active: true,
      target_scope: assignmentTargetScope,
      ...(assignmentTargetMode === 'practice' ? { purpose: 'practice' as const } : {}),
      ...(assignmentTargetMode === 'assessment' ? { purpose: 'assessment', assessment_stage: assessmentStage } : {}),
      ...(assignmentTargetMode === 'assessment' ? {
        retake_policy: assessmentRetakePolicy,
        ...(assessmentRetakePolicy === 'teacher_approval' && assessmentRetakeRemediation.trim()
          ? { retake_remediation_instructions: assessmentRetakeRemediation.trim() }
          : {}),
      } : {}),
      ...(assignmentTargetMode === 'assessment' && assessmentStage === 'apply' && applyStandards.trim()
        ? { standards_snapshot: applyStandards.split(/[\n,;]+/).map((value) => value.trim()).filter(Boolean) }
        : {}),
    } : null;

    if (assignmentTargetScope === 'custom') {
      if (assignmentTargetSelectedStudentIds.length === 0) {
        setAssignmentTargetError(
          assignmentTargetMode === 'split'
            ? 'Select at least one student for the live lesson group.'
            : 'Select at least one student for a targeted assignment.',
        );
        return;
      }
      targetPayload.target_student_ids = assignmentTargetSelectedStudentIds;
      if (payload) payload.target_student_ids = assignmentTargetSelectedStudentIds;
    }

    if (assignmentTargetScope === 'group') {
      if (!assignmentTargetGroupName.trim()) {
        setAssignmentTargetError('Choose a saved subgroup for this assignment.');
        return;
      }
      targetPayload.target_group_name = assignmentTargetGroupName.trim();
      if (payload) payload.target_group_name = assignmentTargetGroupName.trim();
    }

    if (assignmentTargetMode === 'split') {
      if (splitLiveStudentIds.length === 0) {
        setAssignmentTargetError('Choose at least one student for the live lesson group.');
        return;
      }
      if (splitAssignmentStudentIds.length === 0) {
        setAssignmentTargetError('Choose fewer than all students for the live group so someone can receive the self-driven assignment.');
        return;
      }
      targetPayload.target_scope = 'custom';
      targetPayload.target_student_ids = splitLiveStudentIds;
    }

    setRunError('');
    setAssignmentTargetError('');
    setAssigningPlanKey(key);
    try {
      if (assignmentTargetMode === 'live') {
        await startRun(assignmentTargetPlan.id, assignmentTargetSection.id, targetPayload);
      } else if (assignmentTargetMode === 'split') {
        await assignmentsApi.create({
          section_id: assignmentTargetSection.id,
          lesson_plan_id: assignmentTargetPlan.id,
          delivery_mode: 'replay_lesson',
          is_active: true,
          target_scope: 'custom',
          target_student_ids: splitAssignmentStudentIds,
        });
        await startRun(assignmentTargetPlan.id, assignmentTargetSection.id, targetPayload);
      } else if (payload) {
        await assignmentsApi.create(payload);
        let notice = `Assigned ${assignmentTargetPlan.title} to ${assignmentTargetSection.name} as a ${modeLabel} without starting a live lesson.`;
        if (assignmentTargetScope === 'custom') {
          notice = `Assigned ${assignmentTargetPlan.title} to ${assignmentTargetSelectedStudentIds.length} selected student${assignmentTargetSelectedStudentIds.length === 1 ? '' : 's'} in ${assignmentTargetSection.name} as a ${modeLabel} without starting a live lesson.`;
        } else if (assignmentTargetScope === 'group') {
          const groupCount = assignmentTargetSelectedGroupStudents.length;
          notice = `Assigned ${assignmentTargetPlan.title} to subgroup ${assignmentTargetGroupName} (${groupCount} student${groupCount === 1 ? '' : 's'}) in ${assignmentTargetSection.name} as a ${modeLabel} without starting a live lesson.`;
        }
        setTeacherNotice(notice);
      }
      setAssignmentTargetOpen(false);
    } catch (err: any) {
      const message = err?.response?.data?.error || (
        assignmentTargetMode === 'live'
          ? 'Unable to start lesson run. Please try again.'
          : assignmentTargetMode === 'split'
            ? 'Unable to split this launch. Please try again.'
            : 'Unable to create assignment. Please try again.'
      );
      setAssignmentTargetError(message);
      setRunError(message);
    } finally {
      setAssigningPlanKey((current) => (current === key ? null : current));
    }
  }, [
    assignmentTargetGroupName,
    assignmentTargetMode,
    assignmentTargetPlan,
    assignmentTargetScope,
    assignmentTargetSection,
    assignmentTargetSelectedGroupStudents.length,
    assignmentTargetSelectedStudentIds,
    assessmentStage,
    assessmentRetakePolicy,
    assessmentRetakeRemediation,
    applyStandards,
    splitAssignmentStudentIds,
    splitLiveStudentIds,
    startRun,
  ]);

  const plansBySection = useMemo(() => {
    const hookById = new Map<number, Hook>();
    hooks.forEach((hook) => hookById.set(hook.id, hook));

    const bySection = new Map<number, LessonPlan[]>();
    sections.forEach((section) => {
      const sectionPlans = lessonPlans.filter((plan) => {
        if ((plan as any).granted_section_ids?.includes(section.id)) return true;
        const planHook = hookById.get(plan.hook);
        if (!planHook || planHook.is_archived) return false;
        return (planHook.assignments || []).some(
          (assignment) => assignment.section === section.id && assignment.is_active !== false,
        );
      });
      bySection.set(section.id, sectionPlans);
    });

    return bySection;
  }, [hooks, lessonPlans, sections]);

  const activeRuns = useMemo(() => (
    sections.reduce<Array<{ section: Section; run: LessonRun }>>((rows, section) => {
      const run = activeRunsBySection[section.id];
      if (run?.id) {
        rows.push({ section, run });
      }
      return rows;
    }, [])
  ), [activeRunsBySection, sections]);

  const totalAssignedPlans = useMemo(() => (
    sections.reduce((count, section) => count + (plansBySection.get(section.id)?.length || 0), 0)
  ), [plansBySection, sections]);

  const readySections = useMemo(() => (
    sections.filter((section) => {
      const hasPlans = (plansBySection.get(section.id)?.length || 0) > 0;
      const hasActiveRun = Boolean(activeRunsBySection[section.id]?.id);
      return hasPlans && !hasActiveRun;
    }).length
  ), [activeRunsBySection, plansBySection, sections]);

  const selectedSection = useMemo(() => {
    if (!sections.length) return null;
    return sections.find((section) => section.id === selectedSectionId) || sections[0];
  }, [sections, selectedSectionId]);

  const selectedSectionPlans = selectedSection ? (plansBySection.get(selectedSection.id) || []) : [];
  const selectedSectionLessonWeekGroups = groupPlansByCurriculumWeek(selectedSectionPlans);
  const requestedLessonWeek = selectedSection
    ? selectedLessonWeekBySection[selectedSection.id]
    : undefined;
  const activeLessonWeek = selectedSectionLessonWeekGroups.some((group) => group.key === requestedLessonWeek)
    ? requestedLessonWeek
    : selectedSectionLessonWeekGroups[0]?.key;
  const visibleSelectedSectionPlans = selectedSectionLessonWeekGroups.find(
    (group) => group.key === activeLessonWeek,
  )?.plans || [];
  const selectedSectionActiveRun = selectedSection ? activeRunsBySection[selectedSection.id] : undefined;
  const selectedSectionRecentRuns = selectedSection ? (recentRunsBySection[selectedSection.id] || []) : [];

  useEffect(() => {
    if (!selectedSection) {
      setPendingPrintByPlan({});
      return;
    }
    let cancelled = false;
    const plans = plansBySection.get(selectedSection.id) || [];
    void Promise.all(plans.map(async (plan) => {
      try {
        const response = await lessonPlansApi.resourceOperations(plan.id, selectedSection.id);
        const rows = Array.isArray(response.data?.resources) ? response.data.resources : [];
        return [plan.id, rows.filter((row: any) => row.needs_printing && row.print_status === 'pending').length] as const;
      } catch {
        return [plan.id, (plan.resources || []).filter((resource) => resource.needs_printing).length] as const;
      }
    })).then((entries) => {
      if (!cancelled) setPendingPrintByPlan(Object.fromEntries(entries));
    });
    return () => { cancelled = true; };
  }, [selectedSection?.id, plansBySection]);

  const scheduleByPlanSection = useMemo(() => new Map(
    lessonSchedules.map((schedule) => [
      `${schedule.section_id}:${schedule.lesson_plan_id}`,
      schedule.planned_date || '',
    ]),
  ), [lessonSchedules]);

  const updatePlannedDate = async (plan: LessonPlan, section: Section, plannedDate: string) => {
    const key = `${section.id}:${plan.id}`;
    setUpdatingScheduleKey(key);
    setPreparationError('');
    try {
      const response = await lessonPlansApi.updateSchedule(plan.id, section.id, plannedDate || null);
      const nextSchedule = response.data as LessonSchedule;
      setLessonSchedules((current) => {
        const withoutCurrent = current.filter((row) => (
          row.lesson_plan_id !== plan.id || row.section_id !== section.id
        ));
        return nextSchedule.planned_date ? [...withoutCurrent, nextSchedule] : withoutCurrent;
      });
      await loadPreparation();
    } catch (error: any) {
      setPreparationError(error?.response?.data?.detail || 'Unable to save the planned lesson date.');
    } finally {
      setUpdatingScheduleKey('');
    }
  };

  const markQueueItemPrinted = async (item: PrintQueueItem) => {
    const key = `${item.section_id}:${item.lesson_plan_id}:${item.resource_key}`;
    setPrintingResourceKey(key);
    setPreparationError('');
    try {
      const response = await lessonPlansApi.updateResourceOperation(item.lesson_plan_id, item.section_id, {
        resource_key: item.resource_key,
        print_status: 'ready',
        print_quantity: item.quantity,
      });
      const rows = Array.isArray(response.data?.resources) ? response.data.resources : [];
      const pendingCount = rows.filter((row: any) => row.needs_printing && row.print_status === 'pending').length;
      setPendingPrintByPlan((current) => ({ ...current, [item.lesson_plan_id]: pendingCount }));
      setPrintQueue((current) => current.filter((row) => !(
        row.section_id === item.section_id
        && row.lesson_plan_id === item.lesson_plan_id
        && row.resource_key === item.resource_key
      )));
    } catch (error: any) {
      setPreparationError(error?.response?.data?.detail || 'Unable to mark this material as printed.');
    } finally {
      setPrintingResourceKey('');
    }
  };

  const renderPlanScheduleControl = (plan: LessonPlan, section: Section, compact = false) => {
    const key = `${section.id}:${plan.id}`;
    return (
      <label className={compact ? 'text-xs text-gray-600' : 'text-xs font-medium text-muted'}>
        Planned teaching date
        <input
          aria-label={`Planned date for ${plan.title}`}
          className={`ml-2 rounded border px-2 py-1 text-xs ${compact ? 'border-gray-300 bg-white text-gray-800' : 'border-border bg-surface text-text'}`}
          type="date"
          value={scheduleByPlanSection.get(key) || ''}
          disabled={updatingScheduleKey === key}
          onChange={(event) => void updatePlannedDate(plan, section, event.target.value)}
        />
        {updatingScheduleKey === key ? <span className="ml-2">Saving…</span> : null}
      </label>
    );
  };

  const renderLessonActionMenu = (plan: LessonPlan, section: Section, compact = false) => {
    const sectionActiveRun = activeRunsBySection[section.id];
    const sectionHasActiveRun = Boolean(sectionActiveRun?.id);
    const isCurrentRunPlan = Boolean(sectionActiveRun?.lesson_plan === plan.id);
    const assigning = Boolean(assigningPlanKey?.startsWith(`${section.id}:${plan.id}:`));
    return (
      <label className="text-xs font-medium text-muted">
        <span className="sr-only">Lesson actions</span>
        <select
          aria-label={`Actions for ${plan.title}`}
          className={`rounded border px-2 py-1.5 text-sm font-medium ${compact ? 'border-gray-300 bg-white text-gray-800' : 'border-border bg-surface text-text'}`}
          value=""
          disabled={assigning}
          onChange={(event) => {
            const mode = event.target.value as LaunchTargetMode | '';
            if (!mode) return;
            void openAssignmentTargetDialog(plan, section, mode);
          }}
        >
          <option value="">{assigning ? 'Working…' : 'Actions…'}</option>
          <option value="replay_lesson">Assign as lesson</option>
          <option value="replay_exam">Assign as quiz</option>
          <option value="practice">Release as unit practice</option>
          <option value="assessment">Assign as independent assessment</option>
        </select>
      </label>
    );
  };

  const getRunTeachingContext = (run: LessonRun) => {
    const plan = lessonPlans.find((candidate) => candidate.id === run.lesson_plan);
    const hook = hooks.find((candidate) => candidate.id === (run.hook || plan?.hook));
    const currentStep = plan?.steps?.find((step) => Number(step.id) === Number(run.class_current_step));
    const totalSteps = plan?.steps?.reduce((highest, step) => Math.max(highest, Number(step.order) || 0), 0) || 0;
    const stepPosition = currentStep
      ? `${String(currentStep.stage_tag || 'Step').toUpperCase()} · Step ${currentStep.order}${totalSteps ? ` of ${totalSteps}` : ''}`
      : null;
    return { plan, hook, currentStep, stepPosition };
  };

  const printPreparationContent = (
    <section aria-labelledby="print-preparation-heading" className="rounded-2xl border border-border bg-surface p-5 shadow-soft">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">Preparation</p>
          <h2 id="print-preparation-heading" className="mt-1 text-xl font-semibold text-text">Print materials</h2>
          <p className="mt-1 text-sm text-muted">Printable Drive resources appear here when a lesson has a planned teaching date.</p>
        </div>
        <Badge variant={printQueue.length > 0 ? 'warning' : 'success'}>
          {printQueue.length > 0 ? `${printQueue.length} to print` : 'Caught up'}
        </Badge>
      </div>
      {preparationError ? <p role="alert" className="mt-3 rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{preparationError}</p> : null}
      {preparationLoading ? (
        <p className="mt-4 text-sm text-muted">Loading upcoming materials…</p>
      ) : printQueue.length === 0 ? (
        <div className="mt-4 rounded-xl border border-dashed border-border bg-surface-alt/40 px-4 py-4">
          <p className="text-sm font-medium text-text">No printable materials are waiting.</p>
          <p className="mt-1 text-sm text-muted">Set a planned teaching date in an agenda item�s Materials & preview to add its printable resources to this queue.</p>
        </div>
      ) : (
        <div className="mt-4 grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
          {([
            ['overdue', 'Overdue'],
            ['today', 'Today'],
            ['tomorrow', 'Tomorrow'],
            ['upcoming', 'Upcoming'],
          ] as const).map(([bucket, label]) => {
            const items = printQueue.filter((item) => item.bucket === bucket);
            return (
              <div key={bucket} className="rounded-xl border border-border bg-surface-alt/40 p-3">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-text">{label}</h3>
                  <Badge variant={items.length > 0 && bucket !== 'upcoming' ? 'warning' : 'muted'}>{items.length}</Badge>
                </div>
                {items.length === 0 ? (
                  <p className="mt-3 text-xs text-muted">Nothing waiting.</p>
                ) : (
                  <div className="mt-3 space-y-2">
                    {items.map((item) => {
                      const busyKey = `${item.section_id}:${item.lesson_plan_id}:${item.resource_key}`;
                      return (
                        <article key={busyKey} className="rounded-lg border border-border bg-surface p-3">
                          <p className="text-sm font-semibold text-text">{item.title}</p>
                          <p className="mt-1 text-xs text-muted">{item.section_name} · {item.lesson_plan_title}</p>
                          <p className="mt-1 text-xs text-muted">{formatPlannedDate(item.planned_date)} · {item.quantity} {item.quantity === 1 ? 'copy' : 'copies'}</p>
                          {item.teacher_note ? <p className="mt-2 text-xs text-text">{item.teacher_note}</p> : null}
                          <div className="mt-3 flex flex-wrap gap-2">
                            <a className="inline-flex rounded border border-border px-2 py-1 text-xs font-medium text-brand hover:bg-surface-alt" href={item.src} target="_blank" rel="noreferrer">Open file</a>
                            <button
                              type="button"
                              className="rounded bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                              disabled={printingResourceKey === busyKey}
                              onClick={() => void markQueueItemPrinted(item)}
                            >
                              {printingResourceKey === busyKey ? 'Saving…' : 'Mark printed'}
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );

  const content = (
    <>
      <div className="mx-auto w-full space-y-4">
        {!assignmentsOnly && <header className="flex flex-wrap items-end justify-between gap-4"><div><h2 className="text-3xl font-medium">Let’s teach.</h2><p className="mt-2 text-muted">Prepare, teach together, and help students take their next step.</p></div>
          <PageClassSelector><label className="max-w-full text-sm">Class<select className="mt-1 block max-w-full rounded-lg border border-border bg-surface p-2" value={selectedSection?.id || ''} onChange={e => { setSelectedSectionId(Number(e.target.value)); navigate(`/v2/teacher/teach?section=${e.target.value}`, { replace: true }); }}>{sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label></PageClassSelector>
        </header>}
        {teacherNotice && <p role="status">{teacherNotice}</p>}{runError && <p role="alert" className="text-danger">{runError}</p>}
        {loading ? <p role="status">Loading classes…</p> : selectedSection ? <>
          {assignmentsOnly ? <section className="space-y-4" aria-label="Assign independent work">
            <p className="text-sm text-muted">Choose saved content and who receives it. Assigning makes work available independently; it does not start a live lesson or change today's agenda.</p>
            <label className="block">Activity or lesson<select className="mt-1 block w-full rounded-lg border border-border bg-surface p-2" value={assignmentChoice} onChange={e => setAssignmentChoice(e.target.value)}><option value="">Choose saved content</option>{selectedSectionPlans.map(plan => <option key={plan.id} value={plan.id}>{plan.title}</option>)}</select></label>
            {!selectedSectionPlans.length && <p>No saved content is available to this class. <Link className="text-brand underline" to={`/v2/teacher/plan?section=${selectedSection.id}`}>Open Library</Link></p>}
            {selectedSectionPlans.filter(plan => String(plan.id) === assignmentChoice).map(plan => <div key={plan.id} className="space-y-3">
              {renderLessonActionMenu(plan, selectedSection)}
              <details><summary className="cursor-pointer text-sm text-brand">Materials & completion requirements</summary><div className="mt-3 space-y-3">{Boolean(plan.resources?.length) && <Button variant="secondary" onClick={() => setMaterialsPlan(plan)}>Materials ({plan.resources?.length})</Button>}<PlanCompletionSummary plan={plan} /><Link className="text-brand underline" to={`/v2/teacher/lesson-plan/${plan.id}/student-preview`}>Preview activity</Link></div></details>
            </div>)}
          </section> : <>
          <TeachingDay key={selectedSection.id} sectionId={selectedSection.id} onChanged={() => { void loadActiveRuns(sections); void loadRecentRuns(sections); }} materials={id => {
            const plan = lessonPlans.find(p => p.id === id);
            return plan ? <div className="space-y-3">{plan.resources?.length ? <Button variant="secondary" onClick={() => setMaterialsPlan(plan)}>Materials & printing ({plan.resources.length})</Button> : <p className="text-sm text-muted">No attached materials</p>}{renderPlanScheduleControl(plan, selectedSection)}<PlanCompletionSummary plan={plan} /></div> : <p className="text-sm text-muted">Content details are unavailable. Open this activity in Library to review it.</p>;
          }} />
          <details className="rounded-xl border border-border bg-surface p-5"><summary className="cursor-pointer font-medium">Print queue & upcoming preparation</summary><div className="mt-4">{printPreparationContent}</div></details>
          <details className="rounded-xl border border-border bg-surface p-5"><summary className="cursor-pointer font-medium">Recent lessons & review</summary><div className="mt-3 space-y-3">{selectedSectionRecentRuns.map(run => <div key={run.id} className="flex flex-wrap items-center justify-between gap-3 border-t border-border py-3"><div><p>{run.lesson_plan_title}</p><p className="text-sm text-muted">{formatRunStarted(run.started_at)} · {formatRunStatus(run.status)}</p></div><Link className="text-brand underline" to={`/v2/teacher/run/${run.id}`}>Open lesson</Link></div>)}<Link className="block text-brand underline" to="/v2/teacher/unit-day-review">Review work & prepare tomorrow</Link><Link className="block text-brand underline" to="/v2/teacher/prepare">Prepare next lesson</Link></div></details>
          </>}
        </> : <p>No active classes yet. <Link className="text-brand underline" to="/v2/teacher/students">Set up a class</Link></p>}
      </div>
      {materialsPlan && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-2xl rounded-xl border border-border bg-surface shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-muted">Lesson Materials</p>
                <h2 className="mt-1 text-lg font-semibold text-text">{materialsPlan.title}</h2>
                <p className="mt-1 text-sm text-muted">
                  {materialsPlan.resources?.length || 0} material{materialsPlan.resources?.length === 1 ? '' : 's'}
                  {' · '}
                  {(materialsPlan.resources || []).filter((resource) => resource.needs_printing).length} need printing
                </p>
              </div>
              <Button type="button" variant="ghost" onClick={() => setMaterialsPlan(null)}>Close</Button>
            </div>
            <div className="max-h-[70vh] space-y-3 overflow-y-auto px-5 py-4">
              {(materialsPlan.resources || []).map((resource, index) => (
                <div key={`${resource.src}:${index}`} className="rounded-lg border border-border bg-surface-alt/40 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-text">{resource.title}</p>
                      {resource.description ? <p className="mt-1 text-sm text-muted">{resource.description}</p> : null}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="muted">{lessonResourceSourceLabel(lessonResourceSourceType(resource))}</Badge>
                      <Badge variant="muted">{resource.audience === 'teacher' ? 'Teacher only' : resource.audience === 'both' ? 'Teacher + students' : 'Students'}</Badge>
                      {resource.needs_printing ? <Badge variant="warning">Needs printing</Badge> : null}
                    </div>
                  </div>
                  <a className="mt-3 inline-flex text-sm font-medium text-brand underline" href={resource.src} target="_blank" rel="noreferrer">Open material</a>
                </div>
              ))}
              {selectedSection ? (
                <div className="border-t border-border pt-4">
                  <h3 className="mb-3 text-sm font-semibold text-text">Classroom readiness and release</h3>
                  <ResourceOperationsPanel lessonPlanId={materialsPlan.id} sectionId={selectedSection.id} />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
      {assignmentTargetOpen && assignmentTargetPlan && assignmentTargetSection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-2xl rounded-xl border border-border bg-surface shadow-xl">
            <div className="border-b border-border px-5 py-4">
              <p className="text-xs uppercase tracking-[0.18em] text-muted">
                {assignmentTargetMode === 'split'
                  ? 'Split class launch'
                  : assignmentTargetMode === 'live'
                    ? 'Targeted live lesson'
                    : 'Targeted assignment'}
              </p>
              <h2 className="mt-1 text-lg font-semibold text-text">
                {assignmentTargetPlan.title}
              </h2>
              <p className="mt-1 text-sm text-muted">
                {assignmentTargetMode === 'split'
                  ? `Choose who joins live. Everyone else gets a self-driven lesson in ${assignmentTargetSection.name}.`
                  : assignmentTargetMode === 'live'
                  ? `Choose who should join this live lesson in ${assignmentTargetSection.name}.`
                  : `Choose who should receive this non-live ${assignmentModeLabel(assignmentTargetMode)} in ${assignmentTargetSection.name}.`}
              </p>
            </div>
            <div className="space-y-4 px-5 py-4">
              {assignmentTargetMode === 'assessment' ? (
                <label className="block rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-950">
                  <span className="block font-semibold">Hook stage for the evidence document</span>
                  <select
                    className="mt-2 w-full rounded border border-indigo-200 bg-white px-3 py-2 text-sm text-text"
                    value={assessmentStage}
                    onChange={(event) => setAssessmentStage(event.target.value as 'define' | 'do' | 'explain' | 'apply')}
                  >
                    <option value="define">DEFINE</option>
                    <option value="do">DO</option>
                    <option value="explain">EXPLAIN</option>
                    <option value="apply">APPLY</option>
                  </select>
                  <span className="mt-2 block text-xs text-indigo-800">
                    Students complete this independently. Elevate records results and creates a Headrush-ready evidence document; Headrush remains the official grading record.
                  </span>
                  {assessmentStage === 'apply' ? (
                    <span className="mt-3 block">
                      <span className="block font-semibold">Idaho CTE Pathway standard code(s)</span>
                      <input
                        className="mt-1 w-full rounded border border-indigo-200 bg-white px-3 py-2 text-sm text-text"
                        value={applyStandards}
                        onChange={(event) => setApplyStandards(event.target.value)}
                        placeholder="Example: NET-7 (separate multiple codes with commas)"
                      />
                      <span className="mt-1 block text-xs text-indigo-800">Leave blank to use standards already configured on the Hook.</span>
                    </span>
                  ) : null}
                  <span className="mt-3 block">
                    <span className="block font-semibold">Retake access</span>
                    <select
                      className="mt-1 w-full rounded border border-indigo-200 bg-white px-3 py-2 text-sm text-text"
                      value={assessmentRetakePolicy}
                      onChange={(event) => setAssessmentRetakePolicy(event.target.value as 'teacher_approval' | 'disabled')}
                    >
                      <option value="teacher_approval">Teacher approval required</option>
                      <option value="disabled">No retakes unless reassigned</option>
                    </select>
                  </span>
                  {assessmentRetakePolicy === 'teacher_approval' && (
                    <span className="mt-3 block">
                      <span className="block font-semibold">Preparation required before a retake (optional)</span>
                      <textarea
                        className="mt-1 w-full rounded border border-indigo-200 bg-white px-3 py-2 text-sm text-text"
                        rows={3}
                        value={assessmentRetakeRemediation}
                        onChange={(event) => setAssessmentRetakeRemediation(event.target.value)}
                        placeholder="Example: Review the missed concepts and explain what you corrected before requesting another attempt."
                      />
                      <span className="mt-1 block text-xs text-indigo-800">If entered, students must respond to this preparation step before requesting approval.</span>
                    </span>
                  )}
                </label>
              ) : null}
              {(assignmentTargetPlan.resources?.length || 0) > 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-amber-950">Materials readiness</p>
                      <p className="mt-1 text-sm text-amber-900">
                        {assignmentTargetPlan.resources?.length} material{assignmentTargetPlan.resources?.length === 1 ? '' : 's'} for this lesson
                        {' · '}
                        {(assignmentTargetPlan.resources || []).filter((resource) => resource.needs_printing).length} need printing
                      </p>
                    </div>
                    <Button type="button" variant="secondary" size="sm" onClick={() => setMaterialsPlan(assignmentTargetPlan)}>Review materials</Button>
                  </div>
                </div>
              ) : null}
              <div className="grid gap-3 md:grid-cols-3">
                <label className="rounded-lg border border-border bg-surface-alt/60 px-3 py-3 text-sm text-text">
                  <div className="flex items-start gap-2">
                    <Checkbox
                      checked={assignmentTargetScope === 'all'}
                      disabled={assignmentTargetMode === 'split'}
                      onChange={() => setAssignmentTargetScope('all')}
                    />
                    <span>
                      <span className="block font-medium">All students</span>
                      <span className="block text-xs text-muted">
                        {assignmentTargetMode === 'split'
                          ? 'Split launch needs a smaller live group.'
                          : assignmentTargetMode === 'live'
                          ? 'Invite every active student in the section.'
                          : 'Assign to every active student in the section.'}
                      </span>
                    </span>
                  </div>
                </label>
                <label className="rounded-lg border border-border bg-surface-alt/60 px-3 py-3 text-sm text-text">
                  <div className="flex items-start gap-2">
                    <Checkbox
                      checked={assignmentTargetScope === 'custom'}
                      onChange={() => setAssignmentTargetScope('custom')}
                    />
                    <span>
                      <span className="block font-medium">Selected students</span>
                      <span className="block text-xs text-muted">
                        {assignmentTargetMode === 'split'
                          ? 'Choose who stays with you live.'
                          : 'Choose exactly which students should receive it.'}
                      </span>
                    </span>
                  </div>
                </label>
                <label className="rounded-lg border border-border bg-surface-alt/60 px-3 py-3 text-sm text-text">
                  <div className="flex items-start gap-2">
                    <Checkbox
                      checked={assignmentTargetScope === 'group'}
                      onChange={() => setAssignmentTargetScope('group')}
                    />
                    <span>
                      <span className="block font-medium">Saved subgroup</span>
                      <span className="block text-xs text-muted">
                        {assignmentTargetMode === 'split'
                          ? 'This subgroup joins live; others get the assignment.'
                          : 'Use a subgroup like Python or CompTIA.'}
                      </span>
                    </span>
                  </div>
                </label>
              </div>

              {assignmentTargetLoading ? (
                <div className="rounded-lg border border-border bg-surface-alt/60 px-4 py-3 text-sm text-muted">
                  Loading section students…
                </div>
              ) : assignmentTargetScope === 'custom' ? (
                <div className="rounded-lg border border-border bg-surface-alt/40 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-medium text-text">
                      Selected students ({assignmentTargetSelectedStudentIds.length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setAssignmentTargetSelectedStudentIds(assignmentTargetStudents.map((student) => student.id))}
                      >
                        Select all
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setAssignmentTargetSelectedStudentIds([])}
                      >
                        Clear all
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3 max-h-64 space-y-2 overflow-y-auto">
                    {assignmentTargetStudents.length === 0 ? (
                      <p className="text-sm text-muted">No active students are enrolled in this section.</p>
                    ) : assignmentTargetStudents.map((student) => (
                      <label
                        key={`assignment-target-${student.id}`}
                        className="flex items-center justify-between gap-3 rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text"
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={assignmentTargetSelectedStudentIds.includes(student.id)}
                            onChange={() => toggleAssignmentTargetStudent(student.id)}
                          />
                          <span>
                            <span className="block font-medium">{assignmentTargetStudentName(student)}</span>
                            <span className="block text-xs text-muted">
                              {student.group_name ? `Group: ${student.group_name}` : 'No saved subgroup'}
                            </span>
                          </span>
                        </div>
                        {student.email ? <span className="text-xs text-muted">{student.email}</span> : null}
                      </label>
                    ))}
                  </div>
                </div>
              ) : assignmentTargetScope === 'group' ? (
                <div className="rounded-lg border border-border bg-surface-alt/40 p-4">
                  <label className="block text-sm font-medium text-text">
                    Saved subgroup
                    <select
                      className="mt-2 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
                      value={assignmentTargetGroupName}
                      onChange={(event) => setAssignmentTargetGroupName(event.target.value)}
                    >
                      <option value="">{assignmentTargetGroups.length > 0 ? 'Choose a subgroup' : 'No saved subgroups yet'}</option>
                      {assignmentTargetGroups.map((groupName) => (
                        <option key={`assignment-group-${groupName}`} value={groupName}>
                          {groupName}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="mt-3 rounded-lg border border-border bg-surface px-3 py-3">
                    <p className="text-sm font-medium text-text">
                      Preview ({assignmentTargetSelectedGroupStudents.length})
                    </p>
                    {assignmentTargetSelectedGroupStudents.length === 0 ? (
                      <p className="mt-2 text-sm text-muted">
                        {assignmentTargetGroups.length === 0
                          ? 'No saved subgroups are available for this section yet.'
                          : 'Choose a subgroup to preview its students.'}
                      </p>
                    ) : (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {assignmentTargetSelectedGroupStudents.map((student) => (
                          <span
                            key={`assignment-group-preview-${student.id}`}
                            className="rounded-full border border-border bg-surface-alt px-2.5 py-1 text-xs text-text"
                          >
                            {assignmentTargetStudentName(student)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                  {assignmentTargetMode === 'live'
                    ? `This live lesson will be visible to all active students in ${assignmentTargetSection.name}.`
                    : assignmentTargetMode === 'split'
                      ? 'Choose a smaller live group so the rest of the class can receive the self-driven lesson.'
                    : `This assignment will be visible to all active students in ${assignmentTargetSection.name}.`}
                </div>
              )}

              {assignmentTargetMode === 'split' && !assignmentTargetLoading ? (
                <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-950">
                  <span className="font-semibold">{splitLiveStudentIds.length}</span> student{splitLiveStudentIds.length === 1 ? '' : 's'} will join live.
                  {' '}
                  <span className="font-semibold">{splitAssignmentStudentIds.length}</span> student{splitAssignmentStudentIds.length === 1 ? '' : 's'} will receive the self-driven lesson.
                </div>
              ) : null}

              {assignmentTargetError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {assignmentTargetError}
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4">
              <p className="text-xs text-muted">
                {assignmentTargetMode === 'split'
                  ? 'Students not in the live group receive a self-driven lesson automatically.'
                  : assignmentTargetMode === 'live'
                  ? 'Non-targeted students stay in self-paced work and will not see this selective live room.'
                  : 'Non-targeted students stay out of the assignment in phase 1.'}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={closeAssignmentTargetDialog}
                  disabled={Boolean(assigningPlanKey)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    void assignWithoutLive();
                  }}
                  disabled={assignmentTargetLoading}
                >
                  {assigningPlanKey
                    ? (assignmentTargetMode === 'live' || assignmentTargetMode === 'split' ? 'Starting…' : 'Assigning…')
                    : assignmentTargetMode === 'split'
                      ? 'Start split class'
                      : (assignmentTargetMode === 'live' ? 'Start live lesson' : `Assign ${assignmentModeLabel(assignmentTargetMode)}`)}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
  return assignmentsOnly ? content : <OptionalAppShell enabled role="teacher" title="Teach" navItems={teacherV2NavItems('teach')}>{content}</OptionalAppShell>;
};
